import type { Question } from '../../types';
import { trafficLightsAnimations } from './trafficLightsAnimations';
import { priorityRulesAnimations } from './priorityRulesAnimations';
import { hazardPerceptionAnimations } from './hazardPerceptionAnimations';
import { roundaboutAnimations } from './roundaboutAnimations';
import { bicycleAnimations } from './bicycleAnimations';
import { tramAnimations } from './tramAnimations';

const PEDESTRIAN_CROSSING_ANIMATIONS: Array<AnimationPlaybackRecord> = [
  {
    id: 'pc-anim-1',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian-crossings/pedestrian-crossing-1-waiting-crossing.mp4',
    sceneDescription: 'Pedestrian waiting at zebra crossing, vehicle approaching and yielding.',
  },
  {
    id: 'pc-anim-2',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian-crossings/pedestrian-crossing-2-traffic-light.mp4',
    sceneDescription: 'Signalized crossing with pedestrian conflict risk despite green for driver.',
  },
  {
    id: 'pc-anim-3',
    videoUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/pedestrian-crossings/pedestrian-crossing-3-approach-crossing.mp4',
    sceneDescription: 'School crossing approach with vulnerable pedestrians.',
  },
];

/** Minimal shape used by QuestionAnimationPlayer and media sanitization */
export interface AnimationPlaybackRecord {
  id: string;
  videoUrl: string | null;
  sceneDescription: string;
}

function buildAnimationPlaybackMap(): Map<string, AnimationPlaybackRecord> {
  const m = new Map<string, AnimationPlaybackRecord>();
  const put = (rec: AnimationPlaybackRecord) => {
    m.set(rec.id, rec);
  };

  for (const a of trafficLightsAnimations) {
    put({ id: a.id, videoUrl: a.videoUrl, sceneDescription: a.sceneDescription });
  }
  for (const a of priorityRulesAnimations) {
    const url = a.videoUrl ?? a.imageUrl;
    put({
      id: a.id,
      videoUrl: url && url.length > 0 ? url : null,
      sceneDescription: a.sceneDescription,
    });
  }
  for (const a of hazardPerceptionAnimations) {
    put({ id: a.id, videoUrl: a.videoUrl, sceneDescription: a.sceneDescription });
  }
  for (const a of roundaboutAnimations) {
    put({ id: a.id, videoUrl: a.videoUrl, sceneDescription: a.sceneDescription });
  }
  for (const a of bicycleAnimations) {
    put({ id: a.id, videoUrl: a.videoUrl, sceneDescription: a.sceneDescription });
  }
  for (const a of tramAnimations) {
    put({ id: a.id, videoUrl: a.videoUrl, sceneDescription: a.sceneDescription });
  }
  for (const a of PEDESTRIAN_CROSSING_ANIMATIONS) {
    put(a);
  }

  return m;
}

export const ANIMATION_PLAYBACK_MAP = buildAnimationPlaybackMap();

export function resolveAnimationVideoUrl(animationId: string | null | undefined): string | null {
  if (!animationId) return null;
  const url = ANIMATION_PLAYBACK_MAP.get(animationId)?.videoUrl;
  return url && url.length > 0 ? url : null;
}

/**
 * Drops animationId when the registry has no playable URL, and drops empty imageUrl.
 */
export function sanitizeQuestionMedia(q: Question): Question {
  const out = { ...q };
  const aid = out.animationId;
  if (aid != null && String(aid).trim() !== '') {
    if (!resolveAnimationVideoUrl(aid)) {
      delete out.animationId;
    }
  }
  const iu = out.imageUrl;
  if (iu == null || String(iu).trim() === '') {
    delete out.imageUrl;
  }
  return out;
}

export function sanitizeQuestionsMedia(questions: Question[]): Question[] {
  return questions.map(sanitizeQuestionMedia);
}
